/* eslint-disable react/react-in-jsx-scope */
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { isPureFunder } from "../roleAccess";

const LoginForm = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const navigate = useNavigate();
  const { signIn, user, clearSession } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formIsLoading, setFormIsLoading] = useState(false);
  const [authSuccess, setAuthSucces] = useState(false);
  const { t } = useTranslation("auth", {
    keyPrefix: "login",
  });

  const created = searchParams.get("created");

  const title = created
    ? `${t("Compte créé avec succès")} 🎉`
    : t("Déjà un compte ?");
  const description = created
    ? t("Connectez-vous maintenant pour accéder à votre espace et commencer.")
    : t("Accédez à votre espace et gérez vos projets.");

  const formSchema = z.object({
    email: z.string().min(2, { message: t("Adresse email invalide") }),
    password: z.string().min(2, {
      message: t("Le mot de passe doit contenir au moins 2 caractères."),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (user !== null && isPureFunder(user)) {
      clearSession();
      setError("password", {
        type: "server",
        message: t("Email ou mot de passe incorrect"),
      });
    } else if (
      user !== null &&
      user?.status === 1 &&
      !isPureFunder(user) &&
      user?.isVerified != false &&
      authSuccess
    ) {
      toast({
        title: t("Vous êtes connecté"),
      });

      window.location.href = `/dashboard`;
    } else if (user !== null &&
      user?.status === 1 &&
      !user?.roles?.includes("ROLE_CPINTERVENANT") &&
      user?.isVerified !== false) {
      window.location.href = "/submit/appels-a-projets";

    } else if (
      user !== null &&
      user?.status === 1 &&
      !isPureFunder(user) &&
      user?.isVerified == false
    ) {
      // toast({
      //   title: "Erreur",
      //   description:
      //     "Votre compte n’est pas encore vérifié. Vérifiez votre email !",
      //   className: "bg-white text-red-600 border border-red-600 shadow-md",
      // });
      window.location.href = `/verify-error`;
    } else if (user !== null && user?.isVerified != false && authSuccess) {
      window.location.href = `/submit/programs`;
    }
  }, [user]);

  const { setError } = form;

  const [isRemember, setIsRemember] = useState(false);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setFormIsLoading(true);
    const updatedValues = {
      ...values,
      rememberMe: isRemember,
    };
    const { success } = await signIn(
      updatedValues.email,
      updatedValues.password,
      updatedValues.rememberMe,
    );
    setAuthSucces(success);

    // if (success) {
    //   toast({
    //     title: t("Vous êtes connecté"),
    //   });

    // if(user?.status === 1) {
    //   window.location.href = `/dashboard`
    // } /*else if (redirect) {
    //   window.location.href = redirect;
    // } else*/ {
    //   navigate(`/submit/programs`);
    //   window.location.reload();
    // }
    if (!success) {
      // Déclenche une erreur pour le champ mot de passe
      setError("password", {
        type: "server",
        message: t("Email ou mot de passe incorrect"),
      });
    }
    setFormIsLoading(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Mail")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Mail")} {...field} type="text" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Mot de passe")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("Mot de passe")}
                        {...field}
                        type={showPassword ? "text" : "password"}
                      />
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPassword(!showPassword);
                        }}
                        className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center"
                      >
                        {!showPassword ? <EyeOff /> : <Eye />}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-between items-center">
              <label>
                <input
                  type="checkbox"
                  name="_remember_me"
                  onChange={(e) => {
                    setIsRemember(e.target.checked);
                  }}
                />{" "}
                {t("Se souvenir de moi")}
              </label>
              <Link to="/forgot-password" className="underline justify-end">
                {t("Mot de passe oublié ?")}
              </Link>
            </div>
            <Button type="submit" variant={"default"} className="w-full">
              {formIsLoading ? <LoadingSpinner /> : t("Se connecter")}
            </Button>
            <div className="mt-4 text-center">
              <Link
                to="/registration"
                // onClick={(e) => {
                //   e.preventDefault();
                //   localStorage.setItem("code", "Regenerate2024!");
                //   navigate("/sign-up");
                // }}
                // to="/sign-up"
                className="inline-flex items-center gap-1 text-sm text-primary underline hover:text-primary/80 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                {t("Je souhaiterais m'inscrire")}
              </Link>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
